/* The selected venue, as a card anchored to its pin.
 *
 * Separate from `place-row` even though both render a venue and the same three
 * actions, for the reason ADR 0004 §4 gives: one is a 240px card with a hero
 * image floating over a map, the other is a full-width text row in a list. A
 * shared component behind a `variant` prop is how a fifteen-prop component
 * starts.
 *
 * Presentational. It emits; the workspace owns the data and the API calls —
 * including the delete confirmation, which belongs to whoever can also remove
 * the row from the list.
 *
 * Instantiated imperatively by map-canvas (as an AdvancedMarkerElement's
 * content) rather than rendered in a template, so its events are dispatched
 * `composed` to escape the map's shadow root.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { iconActions, shadowReset } from '../../design/base.js';
import { apiUrl } from '../../core/api.js';
import type { Venue } from '../../core/types.js';
import { kindMeta, localityOf } from './kinds.js';
import '../../component/g-button/g-button.js';
import '../../component/g-icon/g-icon.js';

@customElement('venue-card')
export class VenueCard extends LitElement {
  static override styles = [
    shadowReset,
    iconActions,
    css`
      :host {
        display: block;
        /* Containing block for the close button. */
        position: relative;
        width: 240px;
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        background: var(--surface);
        /* Heavier than --shadow-1: this is the one element in the app that floats
           over an arbitrary photographic background, and the system's 1px shadow
           disappears against a satellite tile or a dark park. */
        box-shadow: 0 6px 20px rgb(0 0 0 / 0.28);
        font-family: var(--font-ui);
        /* Google anchors marker content bottom-centre, at the same point as the
           venue's own marker — so without a lift the card sits directly on top
           of the pill it describes and hides it. 36px clears a 26px pill with a
           little air.

           No tail on the card, deliberately: the pill underneath already has one
           pointing at the coordinate, and two nested arrows aimed at the same
           point is one too many. The card reads as attached to the pill by
           proximity, which is enough when nothing else is between them. */
        transform: translateY(-36px);
      }
      .hero {
        display: block;
        width: 100%;
        /* The thumbnails are 3:4 portrait; cropping to a wide strip keeps the
           card compact and still identifies the item. */
        aspect-ratio: 16 / 9;
        border-radius: var(--radius-md) var(--radius-md) 0 0;
        object-fit: cover;
        background: var(--surface-2);
      }
      .close {
        position: absolute;
        top: var(--space-1);
        right: var(--space-1);
        --icon-size: 14px;
        --g-button-pad: 4px;
        border-radius: var(--radius-full);
        /* Legible over both the hero image and a bare card. */
        background: color-mix(in oklab, var(--surface) 82%, transparent);
        backdrop-filter: blur(3px);
      }
      .body {
        padding: var(--space-2) var(--space-3) var(--space-3);
      }
      .name {
        font-size: 0.92rem;
        font-weight: 600;
        line-height: 1.3;
        overflow-wrap: anywhere;
      }
      .sub {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        margin-top: 2px;
        color: var(--text-dim);
        font-size: 0.78rem;
      }
      .sub g-icon {
        --icon-size: 12px;
        color: var(--kc);
      }
      .n {
        font-family: var(--font-mono);
        font-size: 0.7rem;
      }
      .acts {
        align-items: center;
        margin-top: var(--space-3);
      }
      /* The one labelled action; the rest are icons. Watch is why the card is
         open at all, and an unlabelled triangle is not a promise of anything. */
      .watch {
        flex: 1;
        --g-button-pad: 5px var(--space-3);
        border-radius: var(--radius-full);
        background: var(--surface-2);
        font-size: 0.8rem;
      }
      .watch g-icon {
        --icon-size: 13px;
      }
      /* Visited reads as on/off, so it needs a resting state that is visibly
         different rather than a hover tint. */
      .on {
        color: var(--k-stay);
      }
    `,
  ];

  @property({ attribute: false }) venue!: Venue;

  private emit(type: 'watch' | 'toggle-visited' | 'remove' | 'close'): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }));
  }

  private openInGoogleMaps(): void {
    const v = this.venue;
    const q = `${v.lat},${v.lng}`;
    const url =
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` +
      (v.place_id ? `&query_place_id=${encodeURIComponent(v.place_id)}` : '');
    window.open(url, '_blank', 'noopener');
  }

  override render() {
    const v = this.venue;
    if (!v) return nothing;
    const meta = kindMeta(v.kind, v.geocoded);
    const hero = v.reels[0]?.thumb;
    const locality = localityOf(v);
    return html`
      ${hero ? html`<img class="hero" src=${apiUrl(hero)} alt="" loading="lazy" />` : nothing}
      <g-button class="close" icon-only label="Close" @click=${() => this.emit('close')}>
        <g-icon name="x"></g-icon>
      </g-button>
      <div class="body" style="--kc: ${meta.color}">
        <div class="name">${v.name}</div>
        <div class="sub">
          <g-icon name=${meta.icon}></g-icon>
          ${locality}
          ${v.reels.length > 1 ? html`<span class="n">×${v.reels.length}</span>` : nothing}
        </div>
        <div class="acts">
          <g-button class="watch" @click=${() => this.emit('watch')}>
            <g-icon name="play"></g-icon>
            ${v.reels.length > 1 ? `Watch ${v.reels.length}` : 'Watch'}
          </g-button>
          ${v.geocoded
            ? html`<g-button icon-only label="Open in Google Maps" @click=${this.openInGoogleMaps}>
                <g-icon name="external"></g-icon>
              </g-button>`
            : nothing}
          <g-button
            class=${v.visited ? 'on' : ''}
            icon-only
            label=${v.visited ? 'Visited' : 'Mark visited'}
            @click=${() => this.emit('toggle-visited')}
          >
            <g-icon name="check"></g-icon>
          </g-button>
          <g-button
            variant="danger"
            icon-only
            label="Remove from map"
            @click=${() => this.emit('remove')}
          >
            <g-icon name="trash"></g-icon>
          </g-button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'venue-card': VenueCard;
  }
}
